import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useGetOnboardingQuery } from '../store/api/onboardingApi';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Redesign 2026-06: light-only. Dark mode dropped; theme is locked to 'light'.
  const theme = 'light';

  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const userId = user?.id ?? user?.userId ?? null;

  // ---- Activation state -----------------------------------------------------
  // `canGoLive` means "this account has completed go-live": verified business
  // plus a payout wallet. It gates LIVE API KEY CREATION (see Developer.jsx),
  // which is what actually lets a merchant take real money.
  //
  // It deliberately does NOT gate the Test/Live toggle any more. That toggle is
  // only a dashboard view — it adds isTestnet to this merchant's own reads —
  // so blocking it stopped merchants looking at their own live data without
  // preventing anything that moves funds. The control that matters is on the
  // keys, and it stays.
  const isAdmin = user?.role_id === 1 || user?.roleId === 1;
  const isVerified = Boolean(user?.is_verified ?? user?.isVerified);
  const { data: onboarding } = useGetOnboardingQuery(undefined, { skip: !isAuthenticated });
  const goLiveStatus = onboarding?.go_live_status || 'not_started';
  const canGoLive = isAdmin || isVerified || goLiveStatus === 'wallet_added' || goLiveStatus === 'live';
  const goLiveReason = canGoLive
    ? ''
    : 'Verify your business and add a payout wallet to switch to Live.';

  // ---- Mode (persisted per account on this device) --------------------------
  const modeKey = userId ? `maxelpay_mode_${userId}` : 'mode';

  // The saved mode is read SYNCHRONOUSLY, during render, for whichever account
  // is current. It used to start at 'test' and be corrected by an effect after
  // the first render, so every reload in Live briefly ran in Test: pages fired a
  // test request and then a live one, and when the test response landed last
  // the Balance card showed test figures under a LIVE badge. That same first
  // render also let the persist effect write 'test' over the saved 'live'.
  const readSavedMode = (key) => {
    try {
      return localStorage.getItem(key) === 'live' ? 'live' : 'test';
    } catch {
      return 'test';
    }
  };
  const [modeState, setModeState] = useState(() => ({ key: modeKey, mode: readSavedMode(modeKey) }));
  // When the account changes, its own saved mode applies from that render on.
  const mode = modeState.key === modeKey ? modeState.mode : readSavedMode(modeKey);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  // Persist.
  useEffect(() => {
    try {
      localStorage.setItem(modeKey, mode);
    } catch {
      /* storage unavailable: the mode just is not remembered */
    }
  }, [mode, modeKey]);

  // Switching the view is always allowed. Returns true so the Header's existing
  // call site keeps working unchanged.
  const setMode = (next) => {
    setModeState({ key: modeKey, mode: next === 'live' ? 'live' : 'test' });
    return true;
  };

  const toggleMode = () => setMode(mode === 'test' ? 'live' : 'test');
  const toggleTheme = () => {};

  return (
    <ThemeContext.Provider
      value={{ theme, toggleTheme, mode, setMode, toggleMode, canGoLive, goLiveReason }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
